/* eslint-disable max-lines */
import { css } from 'linaria';
import { styled } from 'linaria/react';

type StyledContainerProps = {
  bgColor: string;
};

export const StyledContainer = styled.div<StyledContainerProps>`
  background-color: ${({ bgColor }) => bgColor};
  background-size: cover;
  padding: 32px;
  border-radius: var(--rounding-xl);
  width: 100%;
  position: relative;

  @media (--only-desktop) {
    padding: 32px 40px;
  }

  @media (--only-tablet) {
    padding: 24px;
  }

  @media (--only-mobile) {
    padding: 16px 16px 24px 16px;
  }
`;

export const promoBannerContent = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;

  @media (--to-desktop) {
    margin-bottom: 24px;
  }
`;

export const promoBannerText = css`
  white-space: break-spaces;

  && {
    @media (--only-tablet) {
      font-size: 20px;
      line-height: 23px;
    }

    @media (--only-mobile) {
      font-size: 12px;
      line-height: 14px;
    }
  }
`;

export const promoBannerContentTitle = css`
  white-space: break-spaces;
  && {
    margin-bottom: 8px;

    @media (--only-tablet) {
      font-size: 28px;
      line-height: 30px;
    }

    @media (--only-mobile) {
      font-size: 18px;
      line-height: 22px;
    }
  }
`;

export const promoBannerLogo = css`
  margin-bottom: 20px;
  width: 100%;
`;

export const promoBannerContentImage = css`
  width: 653px;

  @media (--only-desktop) {
    width: 442px;
  }

  @media (--only-tablet) {
    width: 246px;
  }

  @media (--only-mobile) {
    width: 106px;
    position: absolute;
    top: 0;
    right: 0;
    border-top-right-radius: var(--rounding-xl);
  }
`;

export const promoBannerOffersGrid = css`
  display: grid;
  gap: 24px;
  
  @media (--only-tablet) {
    gap: 16px;
  }

  @media (--only-mobile) {
    gap: 12px;
  }
`;

export const PromoBannerOffersGridMap: Record<number, string> = {
  1: css`
    grid-template-columns: 1;

    > div {
      @media (--from-tablet) {
        flex-direction: row;
        column-gap: 50px;
      }
    }

    [data-variant="img"] {
      height: 152px;

      @media (--only-mobile) {
        height: 72px;
      }
    }

    [data-variant="plate"] {
      height: 86px;

      @media (--only-desktop) {
        height: 77px;
      }

      @media (--only-tablet) {
        height: 52px;
      }
    }

    [data-variant="title"] {
      font-size: 28px;
      margin-bottom: 16px;

      @media (--only-desktop) {
        font-size: 24px;
        margin-bottom: 13px;
      }

      @media (--only-tablet) {
        font-size: 22px;
      }
    }

    [data-variant="description"] {
      font-size: 24px;
      line-height: 24px;

      @media (--only-desktop) {
        font-size: 18px;
        line-height: 18px;
      }

      @media (--only-tablet) {
        font-size: 20px;
        line-height: 20px;
      }
    }
  `,
  2: css`
    grid-template-columns: repeat(2, 1fr);

    [data-variant="img"] {
      height: 152px;

      @media (--only-tablet) {
        height: 90px;
      }

      @media (--only-mobile) {
        height: 53px;
      }
    }

    [data-variant="plate"] {
      height: 49px;

      @media (--only-desktop) {
        height: 45px;
      }

      @media (--only-tablet) {
        height: 42px;
      }
    }

    [data-variant="title"] {
      font-size: 22px;
      line-height: 20px;

      @media (--to-largeDesktop) {
        font-size: 18px;
      }
    }

    [data-variant="description"] {
      font-size: 19px;
      line-height: 20px;

      @media (--to-largeDesktop) {
        font-size: 16px;
        line-height: 16px;
      }
    }
  `,
  3: css`
    grid-template-columns: repeat(3, 1fr);

    @media (--to-desktop) {
      grid-template-columns: repeat(2, 1fr);
      
      :first-child {
        grid-column: 1 / 3;
      }
    }

    [data-variant="img"] {
      height: 117px;

      @media (--only-desktop) {
        height: 101px;
      }

      @media (--only-tablet) {
        height: 90px;
      }

      @media (--only-mobile) {
        height: 53px;
      }
    }

    [data-variant="plate"] {
      height: 49px;

      @media (--only-desktop) {
        height: 45px;
      }

      @media (--only-tablet) {
        height: 42px;
      }
    }

    [data-variant="title"] {
      font-size: 18px;
      line-height: 16px;

      @media (--only-desktop) {
        font-size: 14px;
        line-height: 15px;
      }

      @media (--only-tablet) {
        font-size: 18px;
        line-height: 20px;
      }
    }

    [data-variant="description"] {
      font-size: 16px;
      line-height: 16px;

      @media (--only-desktop) {
        font-size: 12px;
        line-height: 12px;
      }
    }

    > div:first-child {
      [data-variant="title"] {
        @media (--only-tablet) {
          font-size: 22px;
        }
      }

      [data-variant="description"] {
        @media (--only-tablet) {
          font-size: 20px;
          line-height: 20px;
        }
      }

      [data-variant="img"] {
        @media (--only-tablet) {
          height: 152px;
        }

        @media (--only-mobile) {
          height: 72px;
        }
      }

      [data-variant="plate"] {
        @media (--only-tablet) {
          height: 51px;
        }
      }
    }
  `,
  4: css`
    grid-template-columns: repeat(4, 1fr);

    @media (--to-desktop) {
      grid-template-columns: repeat(2, 1fr);
    }

    [data-variant="img"] {
      height: 84px;

      @media (--only-desktop) {
        height: 90px;
      }

      @media (--only-tablet) {
        height: 90px;
      }

      @media (--only-mobile) {
        height: 53px;
      }
    }

    [data-variant="plate"] {
      height: 39px;

      @media (--only-desktop) {
        height: 37px;
      }

      @media (--only-tablet) {
        height: 42px;
      }
    }

    [data-variant="title"] {
      font-size: 18px;
      line-height: 16px;

      @media (--only-desktop) {
        font-size: 14px;
        line-height: 15px;
      }
    }

    [data-variant="description"] {
      font-size: 16px;
      line-height: 16px;

      @media (--only-desktop) {
        font-size: 12px;
        line-height: 12px;
      }
    }
  `,
  5: css`
    grid-template-columns: repeat(5, 1fr);

    @media (--only-tablet) {
      grid-template-columns: repeat(6, 1fr);

      :nth-child(1),
      :nth-child(2) {
        grid-column: span 3;
      }

      :nth-child(n + 3) {
        grid-column: span 2;
      }
    }

    @media (--only-mobile) {
      grid-template-columns: repeat(2, 1fr);
      
      :first-child {
        grid-column: 1 / 3;
      }
    }

    [data-variant="img"] {
      height: 84px;

      @media (--only-desktop) {
        height: 80px;
      }

      @media (--only-tablet) {
        height: 97px;
      }

      @media (--only-mobile) {
        height: 53px;
      }
    }

    [data-variant="plate"] {
      height: 39px;

      @media (--only-desktop) {
        height: 33px;
      }

      @media (--only-tablet) {
        height: 42px;
      }
    }

    [data-variant="title"] {
      font-size: 18px;
      line-height: 16px;

      @media (--only-desktop) {
        font-size: 12px;
        line-height: 13px;
      }

      @media (--only-tablet) {
        font-size: 16px;
      }
    }

    [data-variant="description"] {
      font-size: 16px;
      line-height: 16px;

      @media (--only-desktop) {
        font-size: 12px;
        line-height: 12px;
      }

      @media (--only-tablet) {
        font-size: 14px;
        line-height: 14px;
      }
    }

    @media (--only-tablet) {
      > div:nth-of-type(1), 
      > div:nth-of-type(2), {
        [data-variant="title"] {
          font-size: 18px;
        }

        [data-variant="description"] {
          font-size: 16px;
        }
      }
    }

    > div:first-child {
      [data-variant="img"] {
        @media (--only-mobile) {
          height: 72px;
        }
      }
    }
  `,
};

export const PromoBannerOffer = styled.div`
  display: flex;
  background-color: var(--background-primary);
  border-radius: var(--rounding-l);
  padding: 16px;
  height: 152px;
  position: relative;
  flex-direction: column;
  align-items: flex-start;

  @media (--only-mobile) {
    height: 102px;
    padding: 8px;
  }
`;

export const promoBannerOfferWrapper = css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-bottom: 12px;

  @media (--only-mobile) {
    margin-bottom: 8px;
  }
`;

export const promoBannerOfferTitle = css`
  white-space: break-spaces;

  margin-bottom: 6px;

  && {
    @media (--only-mobile) {
      font-size: 12px;
      line-height: 12px;
      margin-bottom: 4px;
    }
  }
`;

export const promoBannerOfferText = css`
  white-space: break-spaces;

  && {
    @media (--only-mobile) {
      font-size: 10px;
      line-height: 10px;
    }
  }
`;

export const promoBannerOfferImage = css`
  height: 100%;
  position: absolute;
  right: 0;
  bottom: 0;
`;

export const promoBannerOfferPlate = css`
  && {
    @media (--only-mobile) {
      height: 28px;
    }
  }
`;
